import React, { useState, useEffect } from 'react';
import {
    Row,
    Col,
    Card,
    Table,
    Button,
    Alert,
    Container,
    Breadcrumb,
    Spinner

} from 'react-bootstrap';
import { FiUpload, FiEye, FiLoader, FiDownload, FiRefreshCw } from 'react-icons/fi';
import toast from 'react-hot-toast';
import Base from '../Base';
import { useNavigate, useParams, NavLink } from "react-router-dom";
import BRCFileUploadModal from './BRCFileUploadModal';
import ManualBRCFileUploadModal from './ManualBRC/ManualBRCFileUploadModal';
import PaginationComponent from '../common/Pagination';
import { downloadErrorFile, getStatusDisplay, retryFileProcess } from './BRCHelper';
import instance from "../../services/baseService";
import { MdOutlineReportGmailerrorred } from "react-icons/md";
import useSidebar from '../../hooks/useSidebar';
const UploadStatusTab = () => {
    // Pagination State
    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [perPage] = useState(25);
    const { collapse } = useSidebar();
    // Data State
    const [uploadData, setUploadData] = useState([]);

    // Loading States
    const [loading, setLoading] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);

    // UI States
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [showManualUploadModal, setShowManualUploadModal] = useState(false);

    // Navigation & Params
    const navigate = useNavigate();
    const { client_id } = useParams();

    // Data transformation functions
    const formatDate = (dateString) => {
        if (!dateString || dateString === null) return '-';
        try {
            return new Date(dateString).toLocaleDateString('en-IN');
        } catch (error) {
            return '-';
        }
    };

    const formatDisplayValue = (value, fallback = '-') => {
        if (value === null || value === undefined || value === '') return fallback;
        return value;
    };

    // Status configuration with navigation flags - Updated to include status 4
    const statusConfig = {
        1: {
            label: "View Report",
            variant: "primary",
            icon: <FiEye className="me-1" />,
            actionType: "view",
            disabled: false
        },
        2: {
            label: "Processing",
            variant: "primary",
            icon: <FiLoader className="me-1 spin" />,
            actionType: "processing",
            disabled: true
        },
        3: {
            label: "Error Report",
            variant: "danger",
            icon: <FiDownload className="me-1" />,
            actionType: "error",
            disabled: false
        },
        4: {
            label: "Validation Error",
            variant: "danger",
            icon: <FiDownload className="me-1" />,
            actionType: "error",
            disabled: false
        },
        5: {
            label: "Processing",
            variant: "primary",
            icon: <FiLoader className="me-1 spin" />,
            actionType: "processing",
            disabled: true
        },
        6: {
            label: "Failed",
            variant: "danger",
            icon: <MdOutlineReportGmailerrorred className="me-1" />,
            actionType: "error",
            disabled: true
        },
        7: {
            label: "Try Again",
            variant: "danger",
            icon: <MdOutlineReportGmailerrorred className="me-1" />,
            actionType: "tryAgain",
            disabled: false
        },
        8: {
            label: "Failed",
            variant: "danger",
            icon: <MdOutlineReportGmailerrorred className="me-1" />,
            actionType: "error",
            disabled: true
        }

    };

    // API Functions
    const fetchUploadStatus = async (page = 1, showLoader = true) => {
        if (showLoader) {
            setLoading(true);
        }
        setError('');

        try {
            // Make actual API call
            const res = await instance.post(`/api/v1/reconciliation/lists/${client_id}`, {
                page: page,
                perpage: perPage,
            });

            if (res.data && res.data.type === 'success') {
                const responseData = res.data.data;
                setUploadData(responseData.lists || []);
                setTotalCount(responseData.total_count || 0);
                setCurrentPage(responseData.page || page);

                if ((responseData.lists?.length === 0 || !responseData.lists) && page === 1) {
                    setSuccess('No upload records found');
                }
            } else {
                throw new Error(res.data?.message || 'Failed to fetch upload status');
            }

        } catch (err) {
            console.error('Fetch Upload Status Error:', err);

            // Handle different error scenarios
            if (err.response) {
                const { status, data } = err.response;
                if (status === 404) {
                    setError('Upload status endpoint not found');
                } else if (status === 403) {
                    setError('Access denied. You don\'t have permission to view the status.');
                } else if (status >= 500) {
                    setError('Server error occurred. Please try again later.');
                } else {
                    setError(data?.message || 'Failed to fetch status. Please try again.');
                }
            } else if (err.request) {
                setError('Network error. Please check your connection and try again.');
            } else {
                setError(err.message || 'Failed to fetch the  status. Please try again.');
            }

            setUploadData([]);
            setTotalCount(0);
        } finally {
            setLoading(false);
            setInitialLoading(false);
        }
    };

    // Event Handlers
    const handleUploadNew = () => {
        try {
            collapse();
            setShowUploadModal(true);
            setError(''); // Clear any existing errors
        } catch (err) {
            setError('Failed to open upload dialog');
        }
    };

    const handleUploadModalClose = (uploadSuccess = false, responseData = null) => {
        setShowUploadModal(false);

        // If upload was successful, refresh the data
        if (uploadSuccess) {
            fetchUploadStatus(currentPage, false);
            // Log the upload response if available
            if (responseData) {
                console.log('Upload completed:', responseData);
            }
        }
    };

    const handleReport = async (item, actionType) => {
        setActionLoading(true);
        setError('');

        try {
            let route;

            switch (actionType) {
                case 'view':
                    route = item.manual_brc
                        ? `/RoDTEP/RoSCTL/reports/manual/${client_id}/${item.id}`
                        : `/RoDTEP/RoSCTL/reports/${client_id}/${item.id}`;
                    break;
                case 'error':
                    downloadErrorFile(item?.client, item?.id, item?.file_name)
                    return;
                case 'processing':
                    setError('File is still processing. Please wait for completion.');
                    return;
                case 'tryAgain':
                    try {
                        const res = await retryFileProcess(item?.client, item?.id);
                        if (res?.type === "success") {
                            await fetchUploadStatus(currentPage);
                        }
                    } catch (e) {
                        console.log("Handled in caller:", e);
                    }
                    return;
                default:
                    route = `/RoDTEP/RoSCTL/reports/${client_id}`;
            }

            // Simulate navigation delay
            await new Promise(resolve => setTimeout(resolve, 500));
            navigate(route);

        } catch (err) {
            console.error('Navigation Error:', err);
            setError('Failed to navigate to report. Please try again.');
        } finally {
            setActionLoading(false);
        }
    };

    const handlePagination = async (page) => {
        setCurrentPage(page);
        await fetchUploadStatus(page);
        // Scroll to top after data is loaded
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const clearMessages = () => {
        setError('');
        setSuccess('');
    };

    // Render action button based on status
    const renderActionButton = (item) => {
        const config = statusConfig[item.status];

        return (
            <Button
                variant={config.variant}
                size="sm"
                className="d-inline-flex align-items-center no-hover"
                onClick={() => handleReport(item, config.actionType)}
                disabled={actionLoading || config.disabled}
            >
                {config.icon}
                {config.label}
            </Button>
        );
    };

    // Load initial data
    useEffect(() => {
        if (client_id) {
            fetchUploadStatus(1);
        } else {
            setError('Client ID is required to fetch upload status.');
            setInitialLoading(false);
        }
    }, [client_id]);

    // Auto-clear success message
    useEffect(() => {
        if (success) {
            const timer = setTimeout(() => setSuccess(''), 5000);
            return () => clearTimeout(timer);
        }
    }, [success]);

    return (
        <Base>
            <Container fluid className="p-4 bg-light min-vh-100">
                <Row>
                    <Col xs={12}>
                        <Breadcrumb>
                            <Breadcrumb.Item linkAs={NavLink} linkProps={{ to: "/" }}>
                                Dashboard
                            </Breadcrumb.Item>
                            <Breadcrumb.Item linkAs={NavLink} linkProps={{ to: "/clients" }}>
                                Clients
                            </Breadcrumb.Item>
                            <Breadcrumb.Item
                                linkAs={NavLink}
                                linkProps={{ to: `/clients/tax-forms/${client_id}` }}
                            >
                                Tax-Forms
                            </Breadcrumb.Item>
                            <Breadcrumb.Item linkAs={NavLink} linkProps={{ to: `/RoDTEP/RoSCTL/${client_id}` }}>
                                RoDTEP/RoSCTL
                            </Breadcrumb.Item>
                            <Breadcrumb.Item active>Upload Status</Breadcrumb.Item>
                        </Breadcrumb>
                    </Col>
                </Row>

                <div>
                    <Row className="align-items-center justify-content-between mb-4">
                        <Col>
                            <h4 className="fw-bold mb-0">Upload Status</h4>
                            <p className="text-muted mb-0">Review and manage upload status.</p>
                        </Col>
                        <Col className="col-auto d-flex gap-2">
                            <Button
                                id='generate_section_id'
                                variant="primary"
                                onClick={() => { collapse(); setShowManualUploadModal(true) }}
                                disabled={loading}
                                className="d-flex align-items-center fw-bold no-hover"
                            >
                                <FiUpload className="me-2" />
                                Manual Recon...
                            </Button>
                        </Col>
                        <Col className="col-auto d-flex gap-2">
                            <Button
                                id='generate_section_id'
                                variant="primary"
                                onClick={handleUploadNew}
                                disabled={loading}
                                className="d-flex align-items-center fw-bold no-hover"
                            >
                                <FiUpload className="me-2" />
                                Start Recon...
                            </Button>
                        </Col>
                    </Row>

                    {/* Success Alert */}
                    {success && (
                        <Alert variant="success" dismissible onClose={clearMessages} className="mb-3">
                            {success}
                        </Alert>
                    )}

                    {/* Error Alert */}
                    {error && (
                        <Alert variant="danger" dismissible onClose={clearMessages} className="mb-3">
                            {error}
                        </Alert>
                    )}

                    {/* Main Content Card */}
                    <Card className="border-0">
                        <Card.Body>
                            {initialLoading ? (
                                <div className="text-center py-5">
                                    <Spinner animation="border" role="status" className="mb-3">
                                        <span className="visually-hidden">Loading...</span>
                                    </Spinner>
                                    <p className="text-muted">Loading upload status...</p>
                                </div>
                            ) : (
                                <>
                                    <div className="table-responsive">
                                        <Table hover className="core-ui-table">
                                            <thead className="table-light">
                                                <tr>
                                                    <th className="text-left" scope="col">USER NAME</th>
                                                    <th className="text-left" scope="col">Start Date</th>
                                                    <th className="text-left" scope="col">End Date</th>
                                                    <th className="text-left" scope="col">Type</th>
                                                    <th className="text-left" scope="col">Created DATE</th>
                                                    <th className="text-left" scope="col">UPLOAD STATUS</th>
                                                    <th className='text-left' scope="col">ACTIONS</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {loading && uploadData.length > 0 && (
                                                    <tr>
                                                        <td colSpan="6" className="text-center py-2">
                                                            <small className="text-muted">
                                                                <Spinner animation="border" size="sm" className="me-2" />
                                                                Updating records...
                                                            </small>
                                                        </td>
                                                    </tr>
                                                )}

                                                {uploadData.length > 0 ? (
                                                    uploadData.map((item) => (
                                                        <tr key={item.id}>
                                                            <td className="fw-medium text-left">
                                                                {formatDisplayValue(item.userName)}
                                                            </td>
                                                            <td className="text-left">
                                                                {formatDisplayValue(item.start_date)}
                                                            </td>
                                                            <td className="text-left">
                                                                {formatDisplayValue(item.end_date)}
                                                            </td>
                                                            <td className="text-left">
                                                                {item.manual_brc ? "Manual" : "Automation"}
                                                            </td>
                                                            <td className="text-left">
                                                                {formatDate(item.created_date)}
                                                            </td>
                                                            <td className='font-bold text-left'>
                                                                {getStatusDisplay(item.status)}
                                                            </td>
                                                            <td className="text-left">
                                                                {renderActionButton(item)}
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan="6" className="text-center py-4 text-muted">
                                                            {error ? 'Unable to load upload records' : 'No upload records found'}
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </Table>
                                    </div>

                                    {/* Pagination */}
                                    {totalCount > perPage && (
                                        <div className="mt-3">
                                            <PaginationComponent
                                                total_count={totalCount}
                                                current_page={currentPage}
                                                per_page={perPage}
                                                handleOnClick={handlePagination}
                                            />
                                        </div>
                                    )}
                                </>
                            )}
                        </Card.Body>
                    </Card>
                </div>

                <BRCFileUploadModal
                    show={showUploadModal}
                    onHide={handleUploadModalClose}
                    clientId={client_id}
                    reFetch={fetchUploadStatus}
                />
                <ManualBRCFileUploadModal
                    show={showManualUploadModal}
                    onHide={() => setShowManualUploadModal(false)}
                    clientId={client_id}
                    reFetch={fetchUploadStatus}
                />
            </Container>
        </Base>
    );
};

export default UploadStatusTab;